import { useEffect, useRef } from 'react';

declare global {
  interface Window {
    fbq: (type: string, event: string) => void;
  }
}

interface PixelProps {
  pixelId: string;
  marketingCookies: boolean;
}

const FacebookPixel = ({ pixelId, marketingCookies }: PixelProps) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const idleCallbackIdRef = useRef<number>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let isMounted = true;

    const loadPixel = () => {
      if (!isMounted) return;

      try {
        const trimmedId = pixelId?.trim();
        // match valid pixel id: 1+ digits
        if (!trimmedId?.match(/^\d+$/) || !marketingCookies) {
          return;
        }

        // Prevent duplicate script injection
        if (document.getElementById('facebook-pixel')) {
          return;
        }

        const script = document.createElement('script');
        script.id = 'facebook-pixel';
        script.async = true;
        script.innerHTML = `
          (function(){try{
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', "${trimmedId}");
            fbq("track", "PageView");
          }catch{/*swallow exception*/}})();
        `;
        document.head.appendChild(script);
        scriptRef.current = script;
      } catch {
        /*swallow exception*/
      }
    };

    // Defer pixel loading with timeout to ensure it fires
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleCallbackIdRef.current = window.requestIdleCallback(loadPixel, { timeout: 2000 });
    } else {
      timeoutIdRef.current = setTimeout(loadPixel, 100);
    }

    // Return cleanup function
    return () => {
      isMounted = false;

      if (idleCallbackIdRef.current !== undefined && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleCallbackIdRef.current);
      }
      if (timeoutIdRef.current !== undefined) {
        clearTimeout(timeoutIdRef.current);
      }

      try {
        if (scriptRef.current?.parentNode) {
          scriptRef.current.parentNode.removeChild(scriptRef.current);
        }
      } catch {
        // Script already removed, safe to ignore
      }
    };
  }, [pixelId, marketingCookies]);

  return null;
};

export default FacebookPixel;
